/* Modal de disponibilidad
El owner modifica la disponibilidad de cada mesa o asiento de forma privadda */
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert, Chip, FormControlLabel, Switch, Tooltip, IconButton, CircularProgress, Paper } from '@mui/material';
import { Refresh, PersonAdd, PersonRemove, ZoomIn, ZoomOut, RestartAlt } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { api } from '../../../services/api';
import { Stage, Layer, Circle, Text, Group } from 'react-konva';

const DisponibilidadModal = ({ open, onClose, zona, eventoId }) => {
  const [layoutData, setLayoutData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asientosSeleccionados, setAsientosSeleccionados] = useState([]);
  const [mesasSeleccionadas, setMesasSeleccionadas] = useState([]);
  const [modoSeleccionMultiple, setModoSeleccionMultiple] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [escala, setEscala] = useState(1);
  const [posicion, setPosicion] = useState({ x: 0, y: 0 });
  const stageRef = useRef();

  const CANVAS_WIDTH = 700;
  const CANVAS_HEIGHT = 500;

  useEffect(() => {
    if (open && zona) {
      cargarLayoutZona();
    }
  }, [open, zona]);

  useEffect(() => {
  if (!open) {
    setAsientosSeleccionados([]);
    setMesasSeleccionadas([]);
    setLayoutData(null);
    setModoSeleccionMultiple(false);
    setEscala(1);
    setPosicion({ x: 0, y: 0 });
  }
}, [open]);

  const cargarLayoutZona = async () => {
    setLoading(true);
    try {
      const response = await api.ubicacionesNew.getLayoutZona(zona.id);
      if (response.status === "success") {
        setLayoutData(response.data);
      } else {
        toast.error("Error al cargar el layout de la zona");
      }
    } catch (error) {
      console.error("Error cargando layout:", error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  const zoomIn = () => {
    const newScale = Math.min(escala * 1.2, 3);
    setEscala(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(escala * 0.8, 0.3);
    setEscala(newScale);
  };

  const resetZoom = () => {
    if (layoutData) {
      centrarCanvas();
    } else {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  };

  const handleDragCanvas = (e) => {
    if (e.target === e.target.getStage()) {
      setPosicion({ x: e.target.x(), y: e.target.y() });
    }
  };

  /* const handleCanvasClick = (e) => {
    if (e.target === e.target.getStage()) {
      setAsientosSeleccionados([]);
    }
  }; */

  const toggleAsientoSeleccion = (asiento) => {
    setAsientosSeleccionados(prev => {
      const yaSeleccionado = prev.find(a => a.id === asiento.id);
      if (yaSeleccionado) {
        return prev.filter(a => a.id !== asiento.id);
      } else {
        if (modoSeleccionMultiple) {
          return [...prev, asiento];
        } else {
          return [asiento];
        }
      }
    });
  };

  const marcarAsientosComoOcupados = async (ocupado = true) => {
    if (asientosSeleccionados.length === 0) {
      toast.warning("Selecciona al menos un asiento");
      return;
    }

    const asientosInvalidos = asientosSeleccionados.filter(asiento => asiento.ocupado === ocupado);
    if (asientosInvalidos.length > 0) {
      const estadoActual = ocupado ? "ocupados" : "disponibles";
      toast.warning(`${asientosInvalidos.length} asiento(s) ya están ${estadoActual}`);
      return;
    }

    setGuardando(true);
    try {
      const asientosIds = asientosSeleccionados.map(a => a.id);

      const response = await api.ubicacionesNew.updateAsientosEstado({
        asientosIds: asientosIds,
        ocupado: ocupado
      });

      if (response.status === "success") {
        setLayoutData(prev => ({
          ...prev,
          ubicacion: {
            ...prev.ubicacion,
            mesas: prev.ubicacion?.mesas?.map(mesa => ({
              ...mesa,
              asientos: mesa.asientos.map(asiento =>
                asientosIds.includes(asiento.id)
                  ? { ...asiento, ocupado }
                  : asiento
              )
            })) || [],
            asientos: prev.ubicacion?.asientos?.map(asiento =>
              asientosIds.includes(asiento.id)
                ? { ...asiento, ocupado }
                : asiento
            ) || []
          }
        }));

        setAsientosSeleccionados([]);

        const mensaje = ocupado
          ? `${asientosIds.length} asiento(s) marcado(s) como ocupado(s)`
          : `${asientosIds.length} asiento(s) marcado(s) como disponible(s)`;

        toast.success(mensaje);
      } else {
        toast.error("Error al actualizar los asientos");
      }
    } catch (error) {
      console.error("Error actualizando asientos:", error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setGuardando(false);
    }
  };

  const AsientoCanvas = ({ asiento, esDeMesa, mesa, index }) => {
    const estaSeleccionado = asientosSeleccionados.find(a => a.id === asiento.id);
    const estaOcupado = asiento.ocupado;

    let posX = asiento.coordX || asiento.coord_x || 0;
    let posY = asiento.coordY || asiento.coord_y || 0;

    if (esDeMesa && mesa && (posX === 0 && posY === 0)) {
      const centroMesaX = mesa.coordX + (mesa.ancho / 2);
      const centroMesaY = mesa.coordY + (mesa.alto / 2);
      const radioAsientos = Math.max(mesa.ancho, mesa.alto) / 2 + 25;

      const angulo = (2 * Math.PI * index) / mesa.asientos.length;
      posX = centroMesaX + radioAsientos * Math.cos(angulo);
      posY = centroMesaY + radioAsientos * Math.sin(angulo);
    }

    if (posX === 0 && posY === 0 && !esDeMesa) {
      const cols = 10;
      posX = (index % cols) * 40 + 20;
      posY = Math.floor(index / cols) * 40 + 20;
    }

    const handleClick = (e) => {
      e.cancelBubble = true;
      toggleAsientoSeleccion(asiento);
    };

    const radius = 15;

    return (
      <Group
        x={posX}
        y={posY}
        onClick={handleClick}
      >
        <Circle
          x={0}
          y={0}
          radius={radius}
          fill={estaSeleccionado ? "#6366f1" : (estaOcupado ? "#5C636A" : "#52c41a")}
          stroke={estaSeleccionado ? "#6366f1" : (estaOcupado ? "#5C636A" : "#52c41a")}
          strokeWidth={estaSeleccionado ? 4 : 2}
        />

        <Text
          x={0}
          y={0}
          text={esDeMesa ? asiento.asiento.replace(/^Mesa \d+-/, '') : asiento.asiento}
          fontSize={10}
          fill="white"
          listening={false}
          offsetX={5}
          offsetY={5}
          fontStyle="bold"
        />
      </Group>
    );
  };

  const toggleMesaSeleccion = (mesa) => {
    setMesasSeleccionadas(prev => {
      const yaSeleccionada = prev.find(m => m.id === mesa.id);
      if (yaSeleccionada) {
        return prev.filter(m => m.id !== mesa.id);
      } else {
        return [mesa];
      }
    });
  };

  const MesaCanvas = ({ mesa, index }) => {
    const centroX = mesa.coordX + mesa.ancho / 2;
    const centroY = mesa.coordY + mesa.alto / 2;
    const radius = Math.min(mesa.ancho, mesa.alto) / 2;

    const estaSeleccionada = mesasSeleccionadas.find(m => m.id === mesa.id);

    const todosOcupados = mesa.asientos?.length > 0 && mesa.asientos.every(a => a.ocupado);

    const tieneAsientosVisibles = mesa.asientos?.some(a => a.visible_cliente);

    const puedeSeleccionarMesa = !tieneAsientosVisibles && !todosOcupados;

    const handleMesaClick = (e) => {
      if (puedeSeleccionarMesa) {
        toggleMesaSeleccion(mesa);
      }
    };

    return (
      <Group key={mesa.id} onClick={handleMesaClick} style={{ cursor: puedeSeleccionarMesa ? 'pointer' : 'default' }}>
        {/* Mesa */}
        <Circle
          x={centroX}
          y={centroY}
          radius={radius}
          fill={todosOcupados ? "#E8E8E8" : (estaSeleccionada ? "#6366f1" : "#8BC34A")}
          stroke={todosOcupados ? "#5C636A" : (estaSeleccionada ? "#6366f1" : "#8BC34A")}
          strokeWidth={2}
        />

        <Text
          x={centroX}
          y={centroY}
          text={`${mesa.mesa}`}
          fontSize={16}
          fill={todosOcupados ? "#5C636A" : (estaSeleccionada ? "white" : "white")}
          listening={false}
          offsetX={String(mesa.mesa).length < 3 ? 18 : 25}
          offsetY={10}
          align="center"
          fontStyle="bold"
        />

        {mesa.asientos?.filter(a => a.visible_cliente).map((asiento, asientoIndex) => (
          <AsientoCanvas
            key={asiento.id}
            asiento={asiento}
            esDeMesa={true}
            mesa={mesa}
            index={asientoIndex}
          />
        ))}
      </Group>
    );
  };

  const ocuparMesaCompleta = async (mesa) => {
    if (!mesa || !mesa.asientos || mesa.asientos.length === 0) return;
    setGuardando(true);
    try {
      const asientosIds = mesa.asientos.map(a => a.id);
      const response = await api.ubicacionesNew.updateAsientosEstado({
        asientosIds: asientosIds,
        ocupado: true
      });
      if (response.status === "success") {
        setLayoutData(prev => ({
          ...prev,
          ubicacion: {
            ...prev.ubicacion,
            mesas: prev.ubicacion?.mesas?.map(m =>
              m.id === mesa.id
                ? { ...m, asientos: m.asientos.map(a => ({ ...a, ocupado: true })) }
                : m
            ) || [],
            asientos: prev.ubicacion?.asientos || []
          }
        }));
        toast.success(`Mesa ${mesa.mesa} ocupada completa`);
      } else {
        toast.error("Error al ocupar la mesa");
      }
    } catch (error) {
      toast.error("Error al conectar con el servidor");
    } finally {
      setGuardando(false);
    }
  };

  const contarAsientos = () => {
    if (!layoutData?.ubicacion) return { total: 0, ocupados: 0, disponibles: 0 };

    let total = 0;
    let ocupados = 0;

    if (layoutData.ubicacion.asientos) {
      const asientosDirectos = layoutData.ubicacion.asientos.filter(a => !a.idmesa);
      total += asientosDirectos.length;
      ocupados += asientosDirectos.filter(a => a.ocupado).length;
    }

    if (layoutData.ubicacion.mesas) {
      layoutData.ubicacion.mesas.forEach(mesa => {
        if (mesa.asientos) {
          total += mesa.asientos.length;
          ocupados += mesa.asientos.filter(a => a.ocupado).length;
        }
      });
    }

    return {
      total,
      ocupados,
      disponibles: total - ocupados
    };
  };

  const stats = contarAsientos();

  const puedeMarcarComoOcupado = asientosSeleccionados.some(a => !a.ocupado);
  const puedeMarcarComoDisponible = asientosSeleccionados.some(a => a.ocupado);

  function calcularBoundingBox(mesas, asientos) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasElements = false;

    mesas?.forEach(mesa => {
      if (mesa.coordX !== undefined && mesa.coordY !== undefined) {
        hasElements = true;
        minX = Math.min(minX, mesa.coordX);
        maxX = Math.max(maxX, mesa.coordX + (mesa.ancho || 80));
        minY = Math.min(minY, mesa.coordY);
        maxY = Math.max(maxY, mesa.coordY + (mesa.alto || 80));
      }
    });

    asientos?.forEach(asiento => {
      if (asiento.coordX !== undefined && asiento.coordY !== undefined) {
        hasElements = true;
        minX = Math.min(minX, asiento.coordX - 15);
        maxX = Math.max(maxX, asiento.coordX + 15);
        minY = Math.min(minY, asiento.coordY - 15);
        maxY = Math.max(maxY, asiento.coordY + 15);
      }
    });

    if (!hasElements) {
      return {
        minX: 0,
        minY: 0,
        maxX: CANVAS_WIDTH,
        maxY: CANVAS_HEIGHT,
        centerX: CANVAS_WIDTH / 2,
        centerY: CANVAS_HEIGHT / 2
      };
    }

    const padding = 50;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    return { minX, minY, maxX, maxY, centerX, centerY };
  }

  function centrarCanvas() {
    if (!layoutData?.ubicacion) return;
    const mesas = layoutData.ubicacion.mesas || [];
    const asientos = layoutData.ubicacion.asientos || [];

    const boundingBox = calcularBoundingBox(mesas, asientos);
    const { minX, minY, maxX, maxY, centerX, centerY } = boundingBox;

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    const scaleX = CANVAS_WIDTH / contentWidth;
    const scaleY = CANVAS_HEIGHT / contentHeight;
    const optimalScale = Math.min(scaleX, scaleY, 1.5);

    const scaledCenterX = centerX * optimalScale;
    const scaledCenterY = centerY * optimalScale;

    const offsetX = (CANVAS_WIDTH / 2) - scaledCenterX;
    const offsetY = (CANVAS_HEIGHT / 2) - scaledCenterY;

    setEscala(optimalScale);
    setPosicion({ x: offsetX, y: offsetY });
  }

  useEffect(() => {
    if (layoutData?.ubicacion && open) {
      setTimeout(() => {
        centrarCanvas();
      }, 100);
    }
  }, [layoutData, open]);

  const mesasNumeradas = layoutData?.ubicacion?.mesas?.filter(
    mesa => mesa.asientos?.some(a => a.visible_cliente)
  ) || [];

  const asientosIndividuales = layoutData?.ubicacion?.asientos?.filter(
    asiento => !asiento.idmesa
  ) || [];

  if (!zona) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '900px',
          borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(44, 62, 80, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        fontWeight: 700,
        fontSize: '18px',
        color: '#0b0060',
        borderBottom: '1px solid #E4E4E7',
        mb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0b0060' }}>
              Administrar Disponibilidad - {zona.ubicacion}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Gestiona la ocupación de asientos en tiempo real
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${stats.disponibles} Disponibles`}
              sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 'bold' }}
              size="small"
            />
            <Chip
              label={`${stats.ocupados} Ocupados`}
              sx={{ bgcolor: '#5C636A', color: 'white', fontWeight: 'bold' }}
              size="small"
            />
            <Tooltip title="Recargar datos">
              <IconButton onClick={cargarLayoutZona} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : layoutData ? (
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Paper elevation={0} sx={{
              width: 340,
              bgcolor: '#F9FAFB',
              borderRadius: '12px',
              p: 3,
              boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
              border: '1px solid #E4E4E7',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              <Typography variant="p" sx={{
                fontWeight: 700,
                fontSize: '18px',
                color: '#5656ff',
                mb: 1
              }}>
                Controles de Disponibilidad
              </Typography>

              {(mesasNumeradas.length > 0 || asientosIndividuales.length > 0) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={modoSeleccionMultiple}
                      onChange={(e) => setModoSeleccionMultiple(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Selección múltiple"
                  sx={{ mb: 2 }}
                />
              )}

              {asientosSeleccionados.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{asientosSeleccionados.length} asiento(s) seleccionado(s)</strong>
                  </Typography>
                </Alert>
              )}

              {mesasSeleccionadas.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  sx={{ mt: 0 }}
                  disabled={guardando}
                  onClick={() => ocuparMesaCompleta(mesasSeleccionadas[0])}
                >
                  Ocupar mesa completa: {mesasSeleccionadas[0].mesa}
                </Button>
              )}

              {(mesasNumeradas.length > 0 || asientosIndividuales.length > 0) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    startIcon={<PersonRemove />}
                    variant="contained"
                    disabled={asientosSeleccionados.length === 0 || !puedeMarcarComoDisponible || guardando}
                    onClick={() => marcarAsientosComoOcupados(false)}
                    sx={{
                      bgcolor: '#10b981',
                      '&:hover': { bgcolor: '#059669' },
                      '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
                    }}
                    fullWidth
                  >
                    {guardando ? 'Guardando...' : 'Marcar Disponible'}
                  </Button>

                  <Button
                    startIcon={<PersonAdd />}
                    variant="contained"
                    disabled={asientosSeleccionados.length === 0 || !puedeMarcarComoOcupado || guardando}
                    onClick={() => marcarAsientosComoOcupados(true)}
                    sx={{
                      bgcolor: '#ef4444',
                      '&:hover': { bgcolor: '#dc2626' },
                      '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
                    }}
                    fullWidth
                  >
                    {guardando ? 'Guardando...' : 'Marcar Ocupado'}
                  </Button>

                  {asientosSeleccionados.length > 0 && (
                    <Button
                      onClick={() => setAsientosSeleccionados([])}
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Limpiar Selección
                    </Button>
                  )}
                </Box>

              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Leyenda
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: '#10b981', borderRadius: '50%' }} />
                    <Typography variant="body2">Disponible</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: '#5C636A', borderRadius: '50%' }} />
                    <Typography variant="body2">Ocupado</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, border: '2px solid #6366f1', borderRadius: '50%' }} />
                    <Typography variant="body2">Seleccionado</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', color: '#5656ff' }}>
                  Mapa de Asientos
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'white', borderRadius: '8px', p: 1 }}>
                  <Button size="small" onClick={zoomOut} sx={{ minWidth: '32px', height: '32px' }}>
                    <ZoomOut />
                  </Button>
                  <Typography variant="body2" sx={{ mx: 1, minWidth: '60px', textAlign: 'center' }}>
                    {Math.round(escala * 100)}%
                  </Typography>
                  <Button size="small" onClick={zoomIn} sx={{ minWidth: '32px', height: '32px' }}>
                    <ZoomIn />
                  </Button>
                  <Button size="small" onClick={resetZoom} sx={{ ml: 1 }}>
                    <RestartAlt />
                  </Button>
                </Box>
              </Box>

              <Paper elevation={0} sx={{
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                border: '2px dashed #E4E4E7',
                width: CANVAS_WIDTH,
                height: CANVAS_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box'
              }}>
                <Stage
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  ref={stageRef}
                  scaleX={escala}
                  scaleY={escala}
                  x={posicion.x}
                  y={posicion.y}
                  draggable
                  onDragEnd={handleDragCanvas}
                  /* onClick={handleCanvasClick}
                  onTap={handleCanvasClick} */
                  style={{ background: 'transparent', cursor: 'grab' }}
                >
                  <Layer>
                    {layoutData.ubicacion?.mesas?.map((mesa, index) => (
                      <MesaCanvas key={mesa.id} mesa={mesa} index={index} />
                    ))}

                    {layoutData.ubicacion?.asientos?.filter(asiento => !asiento.idmesa).map((asiento, index) => (
                      <AsientoCanvas
                        key={asiento.id}
                        asiento={asiento}
                        esDeMesa={false}
                        mesa={null}
                        index={index}
                      />
                    ))}
                  </Layer>
                </Stage>
              </Paper>
            </Box>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ m: 2 }}>
            No se pudo cargar el layout de la zona. Asegúrate de que la zona tenga un diseño configurado.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 2,
        pt: 2,
        justifyContent: 'flex-end',
        bgcolor: '#f8f9fa',
        borderTop: '1px solid #E4E4E7'
      }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '8px',
            bgcolor: '#fff',
            color: '#1e293b',
            fontWeight: 600,
            px: 3,
            py: 1,
            border: '1px solid #E4E4E7',
            '&:hover': { bgcolor: '#f1f5f9' }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisponibilidadModal;